import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../utils/firebase';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // <--- This controls the "Wait"

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true); // Start loading when auth state changes
      
      if (currentUser) {
        try {
          // 1. Fetch the User Profile from Firestore
          const docRef = doc(db, 'users', currentUser.uid);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            // 2. Merge Auth Data with Firestore Data (Role lives here!)
            setUser({ ...currentUser, ...docSnap.data() });
          } else {
            // 3. If no profile exists (e.g. Google Login for first time), create basic one
            const newUserData = {
              email: currentUser.email,
              firstName: currentUser.displayName?.split(' ')[0] || 'User',
              lastName: currentUser.displayName?.split(' ')[1] || '',
              photoURL: currentUser.photoURL,
              role: 'student', // Default role
              createdAt: serverTimestamp(),
            };
            await setDoc(docRef, newUserData);
            setUser({ ...currentUser, ...newUserData });
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
          setUser(currentUser); // Fallback to basic auth user if Firestore fails
        }
      } else {
        setUser(null);
      }
      
      setLoading(false); // <--- Stop loading ONLY after Firestore fetch is done
    });

    return () => unsubscribe();
  }, []);

  const signup = (email, password) => createUserWithEmailAndPassword(auth, email, password);
  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);
  const logout = () => signOut(auth);
  const resetPassword = (email) => sendPasswordResetEmail(auth, email);
  
  const googleLogin = async () => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  };

  const value = {
    user,
    loading,
    signup,
    login,
    logout,
    resetPassword,
    googleLogin
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children} {/* Don't render App until we know the Role */}
    </AuthContext.Provider>
  );
};